// src/pages/MiPerfil.js
// Página de cuenta propia. Accesible para CUALQUIER usuario autenticado,
// sin importar rol ni permisos. Aquí se cambia la contraseña, así que
// nunca debe quedar detrás de un bloqueo.
import React, { useState, useEffect, useRef } from "react";
import supabase from "../supabaseClient";
import { useTenant } from "../context/TenantContext";
import Swal from "sweetalert2";
import Protegido from "../components/Protegido";

// Misma política que Register.js. Si cambia aquí, cambia allá.
const validarPassword = (pass) => {
  if (pass.length < 8) return "La contraseña debe tener al menos 8 caracteres";
  if (!/[A-Z]/.test(pass)) return "Debe incluir al menos una letra mayúscula";
  if (!/[a-z]/.test(pass)) return "Debe incluir al menos una letra minúscula";
  if (!/[0-9]/.test(pass)) return "Debe incluir al menos un número";
  return null;
};

export default function MiPerfil() {
  const { tenant, perfil, recargar } = useTenant();

  const [form, setForm] = useState({
    nombre: "",
    nuevaPassword: "",
    confirmarPassword: "",
  });
  const [verPassword, setVerPassword] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // useRef y no useState: se actualiza de forma síncrona y sí evita
  // el doble clic. Con useState la segunda llamada alcanza a pasar.
  const guardandoRef = useRef(false);

  useEffect(() => {
    if (perfil) {
      setForm((prev) => ({ ...prev, nombre: perfil.nombre || "" }));
    }
  }, [perfil]);

  const emailActual = (() => {
    try {
      const user = JSON.parse(localStorage.getItem("usuario"));
      return user?.email || "";
    } catch {
      return "";
    }
  })();

  const nombreRol =
    perfil?.rol === "super_admin" ? "Super Admin"
    : perfil?.rol === "admin" ? "Administrador"
    : "Empleado";

  const actualizarPerfil = async () => {
    if (guardandoRef.current) return;

    const { nombre, nuevaPassword, confirmarPassword } = form;

    if (!nombre.trim()) {
      return Swal.fire("Campo requerido", "El nombre es obligatorio", "warning");
    }
    if (nuevaPassword) {
      const problema = validarPassword(nuevaPassword);
      if (problema) {
        return Swal.fire("Contraseña insegura", problema, "warning");
      }
      if (nuevaPassword !== confirmarPassword) {
        return Swal.fire("No coinciden", "Las contraseñas no coinciden", "warning");
      }
    }

    guardandoRef.current = true;
    setGuardando(true);

    try {
      // 'nombre' es la única columna de profiles que el navegador puede
      // escribir. Rol, permisos y estado van por backend con service_role.
      const { error: errorPerfil } = await supabase
        .from("profiles")
        .update({ nombre: nombre.trim() })
        .eq("id", perfil.id);

      if (errorPerfil) {
        return Swal.fire("Error", "No se pudo actualizar el nombre", "error");
      }

      if (nuevaPassword) {
        const { error: errorPass } = await supabase.auth.updateUser({
          password: nuevaPassword,
        });
        if (errorPass) {
          return Swal.fire("Error", errorPass.message, "error");
        }
      }

      setForm((prev) => ({ ...prev, nuevaPassword: "", confirmarPassword: "" }));
      setVerPassword(false);
      // Refresca el contexto para que el saludo y el menú muestren el nombre nuevo.
      if (typeof recargar === "function") recargar(true);

      Swal.fire("✅ Actualizado", "Tu perfil fue actualizado correctamente", "success");
    } catch (err) {
      console.error("Error actualizando perfil:", err);
      Swal.fire("Error de conexión", "Verifica tu internet e intenta de nuevo", "error");
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  const estilos = {
    card: {
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: 20,
      marginBottom: 16,
      background: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    label: { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4 },
    input: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      boxSizing: "border-box",
      marginBottom: 12,
    },
    inputBloqueado: {
      width: "100%",
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      boxSizing: "border-box",
      marginBottom: 12,
      background: "#f9fafb",
      color: "#9ca3af",
    },
    // Envoltura para poder poner el ojito encima del input
    campoPassword: { position: "relative" },
    inputPassword: {
      width: "100%",
      padding: "10px 44px 10px 12px", // espacio a la derecha para el botón
      border: "1px solid #d1d5db",
      borderRadius: 8,
      fontSize: 14,
      boxSizing: "border-box",
      marginBottom: 12,
    },
    ojito: {
      position: "absolute",
      right: 10,
      top: 8,
      background: "none",
      border: "none",
      cursor: "pointer",
      fontSize: 18,
      lineHeight: 1,
      padding: 2,
    },
    btnPrimario: {
      width: "100%",
      padding: 12,
      background: guardando ? "#93c5fd" : "#0077B6",
      color: "white",
      border: "none",
      borderRadius: 8,
      fontSize: 15,
      fontWeight: 600,
      cursor: guardando ? "not-allowed" : "pointer",
    },
    ayuda: { fontSize: 12, color: "#9ca3af", marginTop: -6, marginBottom: 14 },
    aviso: {
      fontSize: 12,
      color: "#92400e",
      background: "#fffbeb",
      border: "1px solid #fde68a",
      borderRadius: 8,
      padding: "8px 10px",
      marginTop: -4,
      marginBottom: 14,
    },
  };

  return (
    <Protegido>
      <div className="sw-pagina">
        <div className="sw-pagina-contenido" style={{ maxWidth: 700 }}>
          <div className="sw-header">
            <h1 className="sw-header-titulo">🙋 Mi Perfil</h1>
          </div>
          <p style={{ textAlign: "center", color: "#9ca3af", marginBottom: 24, marginTop: -10 }}>
            {tenant?.nombre || "Cargando..."}
          </p>

          <div style={estilos.card}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: 18 }}>Información personal</h3>

            <label style={estilos.label}>Nombre</label>
            <input
              type="text"
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              style={estilos.input}
            />

            <label style={estilos.label}>Email</label>
            <input type="email" value={emailActual} disabled style={estilos.inputBloqueado} />
            <p style={estilos.ayuda}>
              El correo no se puede cambiar. Si lo necesitas, pídeselo al administrador de tu empresa.
            </p>

            <label style={estilos.label}>Rol</label>
            <input type="text" value={nombreRol} disabled style={estilos.inputBloqueado} />

            <hr style={{ margin: "16px 0", border: "none", borderTop: "1px solid #e5e7eb" }} />
            <h3 style={{ margin: "0 0 16px 0", fontSize: 18 }}>Cambiar contraseña</h3>

            <div style={estilos.aviso}>
              ⚠️ Evita claves fáciles como <strong>123456</strong>, <strong>abcdef</strong> o el nombre
              de tu empresa. Con tu clave se accede a los datos de todos tus clientes.
            </div>

            <label style={estilos.label}>Nueva contraseña</label>
            <div style={estilos.campoPassword}>
              <input
                type={verPassword ? "text" : "password"}
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
                value={form.nuevaPassword}
                onChange={(e) => setForm({ ...form, nuevaPassword: e.target.value })}
                style={estilos.inputPassword}
              />
              <button
                type="button"
                onClick={() => setVerPassword(!verPassword)}
                style={estilos.ojito}
                title={verPassword ? "Ocultar contraseñas" : "Mostrar contraseñas"}
                aria-label={verPassword ? "Ocultar contraseñas" : "Mostrar contraseñas"}
              >
                {verPassword ? "🙈" : "👁️"}
              </button>
            </div>

            <label style={estilos.label}>Confirmar contraseña</label>
            <div style={estilos.campoPassword}>
              <input
                type={verPassword ? "text" : "password"}
                placeholder="Repite la nueva contraseña"
                autoComplete="new-password"
                value={form.confirmarPassword}
                onChange={(e) => setForm({ ...form, confirmarPassword: e.target.value })}
                style={estilos.inputPassword}
              />
            </div>
            <p style={estilos.ayuda}>
              Debe incluir mayúscula, minúscula y número. Ej: MiClave123 · Déjalas vacías si solo
              quieres cambiar tu nombre.
            </p>

            <button onClick={actualizarPerfil} disabled={guardando} style={estilos.btnPrimario}>
              {guardando ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>
      </div>
    </Protegido>
  );
}